// src/components/leitner/TaskDirectAccess.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { taskService } from '../../services/taskService';
import { leitnerService } from '../../services/leitnerService';
import { useAuth } from '../../contexts/AuthContext';
import TaskTakerView from './TaskTakerView';
import TaskShareModal from './TaskShareModal';
import HeaderLogoTitle from '../shared/HeaderLogoTitle';
import UserProfile from '../shared/SharedUserProfile';
import { ProfileModal } from '../shared/ProfileModal';
import { AuthModal } from '../auth/AuthModal';
import { AlertCircle, Lock, Menu, X, Share2, WifiOff, LogIn } from 'lucide-react';

const GUEST_PROGRESS_KEY = 'pb_guest_task_progress';

const getGuestProgress = (bookId, taskId) => {
  try {
    const stored = localStorage.getItem(GUEST_PROGRESS_KEY);
    if (!stored) return null;
    const all = JSON.parse(stored);
    return all[`${bookId}_${taskId}`] || null;
  } catch {
    return null;
  }
};

const saveGuestProgress = (bookId, taskId, progress) => {
  try {
    const stored = localStorage.getItem(GUEST_PROGRESS_KEY);
    const all = stored ? JSON.parse(stored) : {};
    all[`${bookId}_${taskId}`] = progress;
    localStorage.setItem(GUEST_PROGRESS_KEY, JSON.stringify(all));
  } catch (e) {
    console.error('Failed to save guest progress:', e);
  }
};

const TaskDirectAccess = ({ bookData, bookId: propBookId }) => {
  const { bookId: paramBookId, taskId } = useParams();
  const bookId = propBookId || paramBookId || bookData?.id;
  const navigate = useNavigate();
  const { user, userProfile, logout } = useAuth();
  const [task, setTask] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [isGuestMode, setIsGuestMode] = useState(false);
  
  // Modal states
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isOwner = user?.uid === bookData?.ownerId;

  const styling = {
    primaryColor: bookData?.styling?.primaryColor || '#6366f1'
  };

  const customStyling = {
    primaryColor: bookData?.styling?.headerTextColor || '#ffffff'
  };

  const customDomain = !!(bookData?.customDomain || bookData?.customPath);

  // Guest progress update handler
  const handleGuestProgressUpdate = useCallback((questionId, isCorrect, isSubQuestion = false) => {
    setProgress(prev => {
      const key = isSubQuestion ? 'subquestions' : 'questions';
      const currentBox = prev?.[key]?.[questionId]?.box || 1;
      const newBox = isCorrect ? Math.min(currentBox + 1, 4) : 1;
      
      const updated = {
        ...prev,
        [key]: {
          ...(prev?.[key] || {}),
          [questionId]: { box: newBox, lastReviewed: new Date().toISOString() }
        },
        updatedAt: new Date().toISOString()
      };
      
      saveGuestProgress(bookId, taskId, updated);
      return updated;
    });
  }, [bookId, taskId]);

  useEffect(() => {
    const loadTask = async () => {
      try {
        // Load task first to check settings
        const taskData = await taskService.getTask(bookId, taskId);
        
        if (!taskData) {
          setError('Task not found');
          setLoading(false);
          return;
        }
        
        setTask(taskData);

        // Check if guest access is allowed
        const allowGuestAccess = taskData.visibility === 'public' && taskData.requiresSignIn === false;
        
        if (!user && !allowGuestAccess) {
          setLoading(false);
          return;
        }

        if (!user && allowGuestAccess) {
          setIsGuestMode(true);
        }

        // Check access for logged-in users on non-public tasks
        if (user && taskData.visibility !== 'public') {
          const canAccess = await taskService.canAccessTask(bookId, taskId, user.email, user.uid);
          if (!canAccess) {
            setAccessDenied(true);
            setLoading(false);
            return;
          }
        }

        // Load questions
        const allQuestions = await leitnerService.getBookQuestions(bookId);
        const taskQuestions = taskData.questionIds
          .map(id => allQuestions.find(q => q.id === id))
          .filter(Boolean);
        setQuestions(taskQuestions);

        // Load progress
        if (user) {
          const progressData = await taskService.getTaskProgress(bookId, taskId, user.uid);
          setProgress(progressData);
          if (!progressData) {
            await taskService.initializeTaskProgress(bookId, taskId, user.uid, taskData.questionIds);
          }
        } else {
          // Guest mode - load from localStorage
          const guestProgress = getGuestProgress(bookId, taskId);
          if (guestProgress) {
            setProgress(guestProgress);
          } else {
            const initialProgress = {
              questions: taskData.questionIds.reduce((acc, id) => {
                acc[id] = { box: 1, lastReviewed: null };
                return acc;
              }, {}),
              subquestions: {},
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
            setProgress(initialProgress);
            saveGuestProgress(bookId, taskId, initialProgress);
          }
        }

      } catch (err) {
        console.error('Failed to load task:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadTask();
  }, [bookId, taskId, user]);

  // Header Component
  const TaskHeader = () => (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-slate-900 shadow-xl border-b border-white/50">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo & Title */}
          <HeaderLogoTitle
            isOwner={isOwner}
            bookData={bookData}
            customStyling={customStyling}
            navigate={navigate}
          />

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            {/* Share Button */}
            {task && (
              <button
                onClick={() => setShowShareModal(true)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Share Task"
              >
                <Share2 className="h-5 w-5 text-white" />
              </button>
            )}
            
            {/* Guest Sign In Prompt */}
            {isGuestMode ? (
              <button
                onClick={() => setShowAuthModal(true)}
                className="px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors"
              >
                <LogIn className="h-4 w-4" />
                Sign In
              </button>
            ):(
            <UserProfile
              user={user}
              userProfile={userProfile}
              styling={styling}
              bookData={bookData || { headerSettings: { showProfile: true } }}
              isOwner={isOwner}
              navigate={navigate}
              logout={logout}
              isDarkMode={true}
              setShowAuthModal={setShowAuthModal}
              setShowProfileModal={setShowProfileModal}
              mobileMenuOpen={mobileMenuOpen}
              setMobileMenuOpen={setMobileMenuOpen}
            />
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <div className="flex md:hidden items-center gap-2">
            {task && (
              <button
                onClick={() => setShowShareModal(true)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <Share2 className="h-5 w-5 text-white" />
              </button>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-white" />
              ) : (
                <Menu className="h-6 w-6 text-white" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-3 pt-3 border-t border-white/20 space-y-3">
            {isGuestMode && (
              <button
                onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }}
                className="w-full px-3 py-2 bg-amber-500/20 text-amber-400 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              >
                <LogIn className="h-4 w-4" />
                Sign In to Save Progress
              </button>
            )}
            <UserProfile
              user={user}
              userProfile={userProfile}
              styling={styling}
              bookData={bookData || { headerSettings: { showProfile: true } }}
              isOwner={isOwner}
              navigate={navigate}
              logout={logout}
              isDarkMode={true}
              setShowAuthModal={setShowAuthModal}
              setShowProfileModal={setShowProfileModal}
              mobileMenuOpen={mobileMenuOpen}
              setMobileMenuOpen={setMobileMenuOpen}
            />
          </div>
        )}
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Show login prompt only if task requires sign-in
  if (!user && task?.requiresSignIn !== false) {
    return (
      <div className="min-h-screen bg-slate-900">
        <TaskHeader />
        <div className="flex items-center justify-center p-4 min-h-[calc(100vh-80px)]">
          <div className="bg-slate-800 rounded-lg p-8 text-center max-w-md">
            <Lock className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Login Required</h2>
            <p className="text-slate-400 mb-6">Please login to access this task.</p>
            <button
              onClick={() => {
                console.log('Button clicked, setting showAuthModal to true');
                setShowAuthModal(true);
              }}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Login
            </button>
          </div>
        </div>
        
        {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal} 
          defaultView="signin"
          onClose={() => setShowAuthModal(false)} 
        />
      )}
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-slate-900">
        <TaskHeader />
        <div className="flex items-center justify-center p-4 min-h-[calc(100vh-80px)]">
          <div className="bg-slate-800 rounded-lg p-8 text-center max-w-md">
            <AlertCircle className="h-16 w-16 text-amber-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
            <p className="text-slate-400 mb-6">
              You don't have permission to access this task.
            </p>
          </div>
        </div>
        
        {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal} 
          defaultView="signin"
          onClose={() => setShowAuthModal(false)} 
        />
      )}
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="min-h-screen bg-slate-900">
        <TaskHeader />
        <div className="flex items-center justify-center p-4 min-h-[calc(100vh-80px)]">
          <div className="bg-slate-800 rounded-lg p-8 text-center max-w-md">
            <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">{error || 'Task Not Found'}</h2>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <TaskHeader />
      
      <div className="p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Guest Mode Banner */}
          {isGuestMode && (
            <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
              <WifiOff className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-amber-400 font-medium">Guest Mode - Progress saved locally only</p>
                <p className="text-amber-300/70 text-sm mt-1">
                  Your progress is saved on this device only and won't sync across devices.
                </p>
              </div>
            </div>
          )}

          <TaskTakerView
            task={task}
            questions={questions}
            bookId={bookId}
            bookData={bookData}
            progress={progress}
            isGuestMode={isGuestMode}
            onGuestProgressUpdate={handleGuestProgressUpdate}
            onComplete={async (result) => {
              if (!isGuestMode && user) {
                await taskService.saveTaskAttempt(bookId, taskId, user.uid, result);
              }
              if (customDomain) {
                navigate('/?mode=tasks');
              } else {
                navigate(`/books/${bookId}?mode=tasks`);
              }
            }}
            onBack={() => {
              if (customDomain) {
                navigate('/?mode=tasks');
              } else {
                navigate(`/books/${bookId}?mode=tasks`);
              }
            }}
            styling={styling}
          />
        </div>
      </div>

      {/* Modals */}
      {showProfileModal && (
        <ProfileModal 
          onClose={() => setShowProfileModal(false)} 
          onSave={(updatedProfile) => {
            console.log('Profile updated:', updatedProfile);
            setShowProfileModal(false);
          }}
        />
      )}
      
      <AuthModal 
        isOpen={showAuthModal} 
        defaultView="signin"
        onClose={() => setShowAuthModal(false)} 
      />
      
      
      {showShareModal && task && (
        <TaskShareModal
          task={task}
          bookId={bookId}
          bookData={bookData}
          onClose={() => setShowShareModal(false)}
          onUpdate={(updates) => setTask(prev => ({ ...prev, ...updates }))}
        />
      )}
    </div>
  );
};

export default TaskDirectAccess;